import React, {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react";
import {taxAuthBillingApi} from "../../../api/taxAuthBilling.js";
import Button from "react-bootstrap/Button";
import {useSearchParams} from "react-router-dom";
import {Alert, Badge, Card, Col, Container, ListGroup, Row, Spinner, Stack} from "react-bootstrap";
import HeroSection from "../../../components/HeroSection.jsx";

function TaxAuthBillingPage() {
    const MAX_CHECKS = 10;
    const CHECK_INTERVAL = 3000;
    const [billingInfo, setBillingInfo] = useState(null);
    const numOfChecksDone = useRef(0);
    const [refreshing, setRefreshing] = useState(false);
    const [searchParams] = useSearchParams();

    const isLoading = !billingInfo;
    const payoutsEnabled = billingInfo?.stripeAccountInfo?.payoutsEnabled ?? billingInfo?.payoutsEnabled;
    const statusVariant = isLoading ? "info" : payoutsEnabled ? "success" : "warning";
    const statusLabel = isLoading ? "Checking Status" : payoutsEnabled ? "Payouts Enabled" : "Action Required";
    const statusMessage = isLoading
        ? "Fetching your Stripe connection status."
        : payoutsEnabled
            ? "Your Stripe account is connected and payouts are enabled."
            : "Finish onboarding with Stripe to receive payouts from BC Tax Certs.";

    const loadBillingInfo = async () => {
        setRefreshing(true);
        const info = await taxAuthBillingApi.getBillingInfo();
        setBillingInfo(info);
        setRefreshing(false);
        return info;
    };

    useEffect(() => {
        const pollForBillingUpdate = async () => {
            setRefreshing(true);
            const info = await taxAuthBillingApi.getBillingInfo();
            setBillingInfo(info);
            const updatedPayoutsEnabled = info?.stripeAccountInfo?.payoutsEnabled ?? info?.payoutsEnabled;
            if (updatedPayoutsEnabled) {
                setRefreshing(false);
            } else {
                numOfChecksDone.current = numOfChecksDone.current + 1;

                // Schedule the next check if the maximum number of checks is not reached
                if (numOfChecksDone.current < MAX_CHECKS - 1) {
                    setTimeout(pollForBillingUpdate, CHECK_INTERVAL);
                } else {
                    setRefreshing(false);
                }
            }
        };
        if (searchParams.get("stripeCallback") === 'true'){
            pollForBillingUpdate();
        }
        loadBillingInfo();
    }, []);

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="muni"
                eyebrow="Billing"
                title="Stripe Payouts"
                subtitle="Connect your municipality to receive payouts from BC Tax Certs."
                actions={<Badge bg={statusVariant} className="px-3 py-2">{statusLabel}</Badge>}
            />

            <Card className="dashboard-panel mt-4">
                <Card.Body>
                    <Alert variant={statusVariant} className="d-flex align-items-center justify-content-between">
                        <div>
                            <div className="fw-semibold">{statusMessage}</div>
                            <div className="small text-muted">
                                {refreshing ? "Checking your Stripe status..." : "You can refresh anytime after onboarding."}
                            </div>
                        </div>
                        {refreshing && <Spinner size="sm" />}
                    </Alert>

                    <Row className="g-4">
                        <Col md={7}>
                            <Card className="border-0 bg-light h-100">
                                <Card.Body>
                                    <h2 className="h5 mb-3">Onboarding</h2>
                                    {!payoutsEnabled ? (
                                        <>
                                            <p className="mb-3">
                                                Complete Stripe’s onboarding flow to finish verification and add banking details.
                                            </p>
                                            <Button
                                                href={billingInfo?.stripeOnboardingLink}
                                                target="_blank"
                                                rel="noreferrer"
                                                disabled={!billingInfo?.stripeOnboardingLink}
                                            >
                                                Continue Stripe Onboarding
                                            </Button>
                                        </>
                                    ) : (
                                        <p className="mb-0">
                                            You’re all set. If you need to update banking details, use the Stripe Dashboard.
                                        </p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={5}>
                            <Card className="border-0 bg-light h-100">
                                <Card.Body>
                                    <h2 className="h6 text-uppercase text-muted">Onboarding Tips</h2>
                                    <ListGroup variant="flush" className="small">
                                        <ListGroup.Item className="bg-light px-0">
                                            Use the municipality’s legal name and address.
                                        </ListGroup.Item>
                                        <ListGroup.Item className="bg-light px-0">
                                            Skip owners/directors when prompted.
                                        </ListGroup.Item>
                                        <ListGroup.Item className="bg-light px-0">
                                            If asked for a registration number, enter <strong>0000000</strong>.
                                        </ListGroup.Item>
                                    </ListGroup>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Card className="border-0 mt-4">
                        <Card.Body className="p-0">
                            <Stack direction="horizontal" gap={3} className="mb-2">
                                <h2 className="h5 mb-0">Stripe Dashboard</h2>
                                {billingInfo?.dashboardLoginLink && (
                                    <Badge bg="secondary" className="ms-auto">Available</Badge>
                                )}
                            </Stack>
                            <p className="text-muted mb-3">
                                View payouts, balances, and financial reports in Stripe.
                            </p>
                            <div className="d-flex gap-2">
                                <Button
                                    target="_blank"
                                    rel="noreferrer"
                                    href={billingInfo?.dashboardLoginLink}
                                    disabled={!billingInfo?.dashboardLoginLink}
                                    variant="outline-primary"
                                >
                                    Open Stripe Dashboard
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    onClick={loadBillingInfo}
                                    disabled={refreshing}
                                >
                                    Refresh Status
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Card.Body>
            </Card>
        </Container>

    )
}

export default observer(TaxAuthBillingPage);
